import type { CSSInterpolation } from '@emotion/serialize'
import type { Message } from '../../../../../types/mfc'
import { getRoomState } from '../../../../app-state/state.room'
import type { Size } from '../../../../app-state/state.screen'
import {
    calculateNewsSettings,
    showNewsFromFriends,
    showNewsInClubShows,
    showNewsInPrivateShows,
    showsNewsInRooms,
} from '../../../../mfcLogic/api/news'
import {
    aspectRatio,
    calcHeight,
    calcWidth,
    getHeight,
    getPercentage,
    getWidth,
} from '../../../../utilities/dimensions'
import { COMPONENT_HEIGHTS } from '../../../theme'

export const resolveTestId = (message: Message) => {
    const {
        adminAnnouncement,
        chatCard,
        warning,
        sarcasm,
        system,
        error,
        tip,
        pm,
        me,
    } = message
    if (chatCard) return `Chat__MessageChatCard`
    if (warning) return `Chat__MessageWarning`
    if (sarcasm) return `Chat__MessageSarcasm`
    if (system && tip) return `Chat__MessageTip`
    if (system && adminAnnouncement) return `Chat__AdminAnnouncement`
    if (system) return `Chat__MessageSystem`
    if (error) return `Chat__MessageError`
    if (pm) return `Chat__MessagePM`
    if (me) return `Chat__MessageMyself`
    return `Chat__MessageDefault`
}

export const isMessagePM = (message: Message) => {
    const {
        adminAnnouncement,
        confirmation,
        clubShow,
        warning,
        system,
        error,
        tip,
        pm,
        me,
    } = message
    if (confirmation) return
    if (tip) return
    if (clubShow) return
    if (error) return
    if (warning) return
    if (adminAnnouncement) return
    if (system) return
    if (pm) return true
    if (me) return true
}

export type ResolveStyleStyles = {
    me?: CSSInterpolation[]
    they?: CSSInterpolation[]
    emote?: CSSInterpolation[]
    sarcasm?: CSSInterpolation[]
    notification?: CSSInterpolation[]
    warning?: CSSInterpolation[]
    error?: CSSInterpolation[]
    system?: CSSInterpolation[]
    adminAnnouncement?: CSSInterpolation[]
    clubShow?: CSSInterpolation[]
    tip?: CSSInterpolation[]
    shareTip?: CSSInterpolation[]
    confirmation?: CSSInterpolation[]
    message?: CSSInterpolation[]
}

export const resolveStyle = (message: Message, styles: ResolveStyleStyles) => {
    const {
        adminAnnouncement,
        clubShow,
        warning,
        sarcasm,
        system,
        emote,
        error,
        tip,
        pm,
        me,
        confirmation,
    } = message

    if (emote) {
        if (!pm) return ([] as CSSInterpolation[]).concat(styles.emote)
        if (me)
            return ([] as CSSInterpolation[])
                .concat(styles.me)
                .concat(styles.emote)
        return ([] as CSSInterpolation[])
            .concat(styles.they)
            .concat(styles.emote)
    }
    if (sarcasm) {
        if (!pm) return ([] as CSSInterpolation[]).concat(styles.sarcasm)
        if (me)
            return ([] as CSSInterpolation[])
                .concat(styles.me)
                .concat(styles.sarcasm)
        return ([] as CSSInterpolation[])
            .concat(styles.they)
            .concat(styles.sarcasm)
    }
    if (confirmation)
        return ([] as CSSInterpolation[]).concat(styles.confirmation)
    if (tip) return ([] as CSSInterpolation[]).concat(styles.tip)
    if (clubShow) return ([] as CSSInterpolation[]).concat(styles.clubShow)
    if (error) return ([] as CSSInterpolation[]).concat(styles.error)
    if (warning) return ([] as CSSInterpolation[]).concat(styles.warning)
    if (adminAnnouncement)
        return ([] as CSSInterpolation[]).concat(styles.adminAnnouncement)
    if (system) return ([] as CSSInterpolation[]).concat(styles.system)
    if (me) return ([] as CSSInterpolation[]).concat(styles.me)
    if (pm) return ([] as CSSInterpolation[]).concat(styles.they)
    return ([] as CSSInterpolation[]).concat(styles.message)
}

/** ensure video is at least 320 px wide */
const minVideoPercentage = (width: number) => getPercentage(320, width)

/** ensure chat is at least 250 px wide */
const maxVideoPercentage = (width: number) =>
    width ? Math.floor(100 - (250 * 100) / width) : 100

/** results in chat ~300-400 px wide */
export const defaultVideoPercentage = (width: number) =>
    width
        ? width < 960
            ? 60
            : width < 1440
              ? 70
              : Math.floor(100 - (400 * 100) / width)
        : 100

export const getDivision = (x: number, width: number) => {
    const percentage = getPercentage(x, width)
    const minimum = minVideoPercentage(width)
    const maximum = maxVideoPercentage(width)
    return Math.max(Math.min(percentage, maximum), minimum)
}

export const getDefaultDivision = (width: number, height: number) => {
    const defaultDivision = defaultVideoPercentage(width)
    const maxVideoWidth = (width * defaultDivision) / 100
    const { width: nextVideoWidth = 0 } = calcVideoDimensions(
        maxVideoWidth,
        height,
    )
    const nextDivision = getPercentage(nextVideoWidth, width)
    return nextDivision
}

export const calcVideoDimensions = (
    portalWidth: number,
    portalHeight: number,
    vertical?: boolean,
    fullWindow?: boolean,
    overrideSize?: Size,
): Size => {
    if (!portalWidth || !portalHeight) {
        return { width: undefined, height: undefined }
    }

    if (vertical) {
        return { width: portalWidth, height: calcHeight(portalWidth) }
    }

    if (fullWindow) {
        if (typeof window === 'undefined') {
            return { width: undefined, height: undefined }
        }

        const windowHeight = overrideSize?.height ?? getHeight()
        const windowWidth = overrideSize?.width ?? getWidth()
        // console.log('[calcVideoDimensions]', {
        //     windowWidth,
        //     windowHeight,
        //     check: calcWidth(windowHeight) > windowWidth,
        //     calcWidth: calcWidth(windowHeight),
        //     calcHeight: calcHeight(windowWidth),
        // })
        if (calcWidth(windowHeight) > windowWidth) {
            return { width: windowWidth, height: calcHeight(windowWidth) }
        }
        return { width: calcWidth(windowHeight), height: windowHeight }
    }

    const { actionsBar, chatHeader } = COMPONENT_HEIGHTS
    const maxAvailableHeight = portalHeight - chatHeader - actionsBar
    const { height, width } = aspectRatio({
        height: maxAvailableHeight,
        width: portalWidth,
    })
    return { width, height }
}

export const filterNewsMessages = (
    messages: Array<Message> = [],
    news: number,
    streamType: string,
    friendsListIds: number[],
) => {
    const newsSettings = calculateNewsSettings(news)
    const showNews = showsNewsInRooms(newsSettings.FCNEWSOPT_IN_CHAN)
    const showNewsInClubs = showNewsInClubShows(
        newsSettings.FCNEWSOPT_IN_CHAN_NOGRP,
    )
    const showNewsInPrivates = showNewsInPrivateShows(
        newsSettings.FCNEWSOPT_IN_CHAN_NOPVT,
    )
    const showFriendsNews = showNewsFromFriends(
        newsSettings.FCNEWSOPT_ADDFRIENDS_OFF,
    )
    const noNews =
        showNews === false ||
        (streamType === 'club' && showNewsInClubs === false) ||
        (streamType === 'group' && showNewsInClubs === false) ||
        (streamType === 'voyeur' && showNewsInPrivates === false) ||
        (streamType === 'private' && showNewsInPrivates === false)
    const friendsListIdsSet = new Set(friendsListIds)

    return messages.filter((msg) => {
        if (!msg) {
            const state = getRoomState()
            console.error(
                '[filterNewsMessages] No message found, this should not be happening.',
                JSON.stringify({ state, messages }, null, 4),
            )
            return false
        }

        if (!msg.news) return true
        if (noNews) return false
        if (showFriendsNews === false) {
            return !friendsListIdsSet.has(msg?.user?.id)
        }
        return true
    })
}

// export const filterInlinePMs = (
//     messages: Array<Message> = [],
//     showInlinePM: boolean,
// ) => {
//     return messages.filter(
//         ({ pm, pmCommand }) => !pm || showInlinePM || pmCommand,
//     )
// }
export const filterOnlyTips = (
    messages: Array<Message> = [],
    onlyTips: boolean,
) => {
    return messages.filter(({ tip }) => !onlyTips || tip)
}

export const filterMessages = (
    messages: Array<Message> = [],
    news: number,
    streamType: string,
    // showInlinePM: boolean,
    friendsListIds: Array<number>,
    onlyTips = false,
) => {
    const tipFilteredMessages = filterOnlyTips(messages, onlyTips)
    const noNews = filterNewsMessages(
        tipFilteredMessages,
        news,
        streamType,
        friendsListIds,
    )
    return noNews //filterInlinePMs(noNews, showInlinePM)
}
